import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Link,
  InputAdornment,
  IconButton,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Fade,
  Stack,
  useTheme,
  useMediaQuery
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { 
  EmailOutlined, 
  LockOutlined, 
  Visibility, 
  VisibilityOff,
  AutoGraph,
  Psychology
} from '@mui/icons-material';
import { Hub, Speed, Security, Bolt, TrendingUp } from "@mui/icons-material";
import { setTokens, setUser, initializeAuth, isAuthenticated } from '../services/authService';
import { API_BASE } from '../api/index.js';

// Branding Palette from Reference
const BRAND = {
  primary: "#008080", // Teal
  secondary: "#034A7F", // Navy
  textMain: "#1B2559",
  textSecondary: "#A3AED0",
  bgLight: "#F4F7FE",
  white: "#FFFFFF"
};

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();
  const location = useLocation();
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // 1. UPDATE: Redirection for already authenticated users
  useEffect(() => {
    if (isAuthenticated()) {
      // Redirects to /dashboard-analyzer instead of /dashboard
      const from = location.state?.from?.pathname || '/dashboard-analyzer';
      navigate(from, { replace: true });
    }
  }, [navigate, location]);

  const validateForm = () => {
    const newErrors = {};
    if (!email.trim()) newErrors.email = "Business email is required";
    else if (!/\S+@\S+\.\S+/.test(email)) newErrors.email = "Invalid email format";
    if (!password) newErrors.password = "Password is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleLogin = async () => {
    if (!validateForm()) return;
    try {
      setLoading(true);
      setErrors({});
      
      const response = await fetch(`${API_BASE}/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Authentication failed");

      setTokens(data.data.tokens.accessToken, data.data.tokens.refreshToken);
      setUser(data.data.user);
      initializeAuth();

      // 2. UPDATE: Redirection after successful login action
      const from = location.state?.from?.pathname || '/dashboard-analyzer';
      navigate(from, { replace: true });

    } catch (error) {
      setErrors({ general: error.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: BRAND.white, overflow: "hidden" }}>
      
      {/* LEFT SECTION: AUTHENTICATION FORM */}
      <Box sx={{ 
        flex: { xs: 1, md: 1, lg: 0.8 }, 
        display: "flex", 
        flexDirection: "column", 
        justifyContent: "center",
        alignItems: "center", 
        px: { xs: 3, sm: 8, md: 10, lg: 15 },
        position: "relative",
        zIndex: 10
      }}>
        <Box sx={{ width: "100%", maxWidth: "420px" }}>
          {/* Logo */}
          <Box sx={{ mb: 6, textAlign: isMobile ? 'center' : 'left' }}>
            <a href="https://lendingx.ai" target="_blank" rel="noopener noreferrer" title="You will be redirect to lendingx.ai" style={{ display: "inline-block" }}>
              <img 
                src="https://lendingx.ai/wp-content/uploads/2026/03/cropped-Png-300x79.png" 
                alt="LendingX Logo" 
                style={{ marginBottom: "32px" }} 
              />
            </a>
            <Typography variant="h4" sx={{ fontWeight: 800, color: BRAND.textMain, mb: 1, letterSpacing: "-1px" }}>
              Live Sandbox
            </Typography>
            <Typography variant="body1" sx={{ color: BRAND.textSecondary, fontWeight: 500 }}>
              Access the Credit Intelligence Services
            </Typography>
          </Box>

          {/* Login Fields */}
          <Stack spacing={3}>
            <Box>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 700, color: BRAND.textMain }}>Email Address</Typography>
              <TextField
                fullWidth
                placeholder="mail@lendingx.ai"
                variant="outlined"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                error={!!errors.email}
                helperText={errors.email}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><EmailOutlined sx={{ color: BRAND.textSecondary, fontSize: 20 }} /></InputAdornment>,
                }}
                sx={{ 
                  "& .MuiOutlinedInput-root": { borderRadius: "14px", bgcolor: BRAND.bgLight, border: "none" },
                  "& fieldset": { border: "none" }
                }}
              />
            </Box>

            <Box>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 700, color: BRAND.textMain }}>Password</Typography>
              <TextField
                fullWidth
                type={showPassword ? "text" : "password"}
                placeholder="Min. 8 characters"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                error={!!errors.password}
                helperText={errors.password}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><LockOutlined sx={{ color: BRAND.textSecondary, fontSize: 20 }} /></InputAdornment>,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ 
                  "& .MuiOutlinedInput-root": { borderRadius: "14px", bgcolor: BRAND.bgLight, border: "none" },
                  "& fieldset": { border: "none" }
                }}
              />
            </Box>
          </Stack>

          {/* Actions */}
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", my: 3 }}>
            <FormControlLabel
              control={<Checkbox size="small" sx={{ color: BRAND.primary, '&.Mui-checked': { color: BRAND.primary }}} />}
              label={<Typography variant="body2" sx={{ fontWeight: 600, color: BRAND.textMain }}>Stay logged in</Typography>}
            />
            <Link onClick={() => navigate("/forgot-password")} sx={{ cursor: "pointer", color: BRAND.primary, fontWeight: 700, textDecoration: "none", fontSize: "0.85rem" }}>
              Forgot password?
            </Link>
          </Box>

          {errors.general && (
            <Fade in={!!errors.general}>
              <Box sx={{ p: 2, mb: 3, bgcolor: "#FFF5F5", border: "1px solid #FFD1D1", borderRadius: "12px" }}>
                <Typography variant="body2" color="#E53E3E" textAlign="center" fontWeight="600">{errors.general}</Typography>
              </Box>
            </Fade>
          )}

          <Button
            fullWidth
            variant="contained"
            disabled={loading}
            onClick={handleLogin}
            sx={{
              bgcolor: BRAND.secondary,
              color: "white",
              py: 2,
              borderRadius: "14px",
              textTransform: "none",
              fontSize: "1rem",
              fontWeight: 700,
              boxShadow: "0px 12px 24px rgba(3, 74, 127, 0.25)",
              transition: "all 0.3s ease",
              "&:hover": { bgcolor: BRAND.primary, boxShadow: "none", transform: "translateY(-1px)" },
            }}
          >
            {loading ? <CircularProgress size={24} sx={{ color: "white" }} /> : "Sign In"}
          </Button>

          <Typography variant="body2" align="center" sx={{ mt: 5, color: BRAND.textSecondary, fontWeight: 600 }}>
            Don't have access? <Link onClick={() => navigate("/register")} sx={{ color: BRAND.primary, cursor: "pointer", fontWeight: 800, textDecoration: "none" }}>Create Account</Link>
          </Typography>
        </Box>

        {/* <Typography variant="caption" sx={{ position: "absolute", bottom: 24, color: BRAND.textSecondary, fontWeight: 500 }}>
          © 2026 LendingX. Licensed for Institutional Use.
        </Typography> */}
      </Box>

      {/* RIGHT SECTION: CREATIVE INTELLIGENCE PANEL */}
      <Box sx={{ 
        flex: { xs: 0, md: 1, lg: 1.2 }, 
        display: { xs: "none", md: "flex" },
        position: "relative",
        background: `linear-gradient(145deg, ${BRAND.secondary} 0%, #011627 100%)`,
        justifyContent: "center",
        alignItems: "center",
        overflow: "hidden",
        p: 6
      }}>
        <Box sx={{
          position: "absolute", width: "100%", height: "100%",
          backgroundImage: `radial-gradient(circle at 2px 2px, rgba(255,255,255,0.05) 1px, transparent 0)`,
          backgroundSize: "40px 40px",
          animation: "meshMovement 60s linear infinite",
          "@keyframes meshMovement": { "0%": { transform: "translate(0, 0)" }, "100%": { transform: "translate(-40px, -40px)" } }
        }} />

        <Box sx={{
          position: "absolute", top: "20%", left: "10%", width: "300px", height: "300px",
          bgcolor: BRAND.primary, filter: "blur(120px)", opacity: 0.15, borderRadius: "50%"
        }} />

        <Box sx={{ position: "relative", zIndex: 5, maxWidth: "540px", width: "100%" }}>
          <Box sx={{ 
            bgcolor: "rgba(255, 255, 255, 0.03)",
            backdropFilter: "blur(20px)",
            borderRadius: "48px",
            p: 6,
            border: "1px solid rgba(255, 255, 255, 0.1)",
            boxShadow: "0 40px 80px rgba(0,0,0,0.3)",
            position: "relative"
          }}>
            
            {/* <Box sx={{ 
              position: "absolute", top: -20, right: 40,
              bgcolor: BRAND.primary, px: 2, py: 1, borderRadius: "12px",
              display: "flex", alignItems: "center", gap: 1,
              animation: "floatAnim 4s ease-in-out infinite",
              boxShadow: "0 10px 20px rgba(0,0,0,0.2)",
              "@keyframes floatAnim": { "0%, 100%": { transform: "translateY(0)" }, "50%": { transform: "translateY(-10px)" } }
            }}>
              <AutoGraph sx={{ color: "white", fontSize: 18 }} />
              <Typography variant="caption" sx={{ color: "white", fontWeight: 800 }}>TRUST SCORE: 98%</Typography>
            </Box> */}

            <Psychology sx={{ fontSize: 64, color: BRAND.primary, mb: 3 }} />
            
            <Typography variant="h3" sx={{ 
              color: BRAND.white, fontWeight: 800, mb: 2, lineHeight: 1.1, letterSpacing: "-1.5px" 
            }}>
              AI-Powered <br /> Credit Intelligence
            </Typography>
            
            <Typography variant="h6" sx={{ color: "rgba(255,255,255,0.7)", fontWeight: 400, mb: 4, lineHeight: 1.6 }}>
              Unlock hyper-accurate lending decisions with real-time behavioral insights and predictive modeling.
            </Typography>

            {/* <Stack direction="row" spacing={2} sx={{ flexWrap: "wrap", gap: 1 }}>
              {[
                { label: '360° Data Unification', icon: <AutoGraph sx={{ fontSize: 14 }} /> },
                { label: 'Risk Engine', icon: <AutoGraph sx={{ fontSize: 14 }} /> },
                { label: 'Fraud Shield', icon: <Security sx={{ fontSize: 14 }} /> },
                { label: 'Instant Decisions', icon: <Psychology sx={{ fontSize: 14 }} /> },
                { label: 'High-Conversion Prospecting', icon: <Psychology sx={{ fontSize: 14 }} /> }
              ].map((item) => (
                <Box key={item.label} sx={{ 
                  display: "flex", alignItems: "center", gap: 1,
                  px: 2, py: 1, borderRadius: "30px", 
                  bgcolor: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)"
                }}>
                  <Box sx={{ color: BRAND.primary }}>{item.icon}</Box>
                  <Typography sx={{ color: BRAND.white, fontSize: "0.7rem", fontWeight: 700, letterSpacing: "0.5px" }}>
                    {item.label.toUpperCase()}
                  </Typography>
                </Box>
              ))}
            </Stack> */}

            <Stack direction="row" spacing={2} sx={{ flexWrap: "wrap", gap: 1 }}>
              {[
                { label: '360° Data Unification', icon: <Hub sx={{ fontSize: 14 }} /> },
                { label: 'Risk Engine', icon: <Speed sx={{ fontSize: 14 }} /> },
                { label: 'Fraud Shield', icon: <Security sx={{ fontSize: 14 }} /> },
                { label: 'Instant Decisions', icon: <Bolt sx={{ fontSize: 14 }} /> },
                { label: 'High-Conversion Prospecting', icon: <TrendingUp sx={{ fontSize: 14 }} /> }
              ].map((item) => (
                <Box
                  key={item.label}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: 2,
                    py: 1,
                    borderRadius: "30px",
                    bgcolor: "rgba(255,255,255,0.06)",
                    border: "1px solid rgba(255,255,255,0.1)"
                  }}
                >
                  <Box sx={{ color: BRAND.primary }}>{item.icon}</Box>
                  <Typography
                    sx={{
                      color: BRAND.white,
                      fontSize: "0.7rem",
                      fontWeight: 700,
                      letterSpacing: "0.5px"
                    }}
                  >
                    {item.label.toUpperCase()}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}